import React from 'react';
import { Box, Typography, Chip, Table, TableHead, TableBody, TableRow, TableCell, Tooltip, Alert, Button } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WifiOffIcon from '@mui/icons-material/WifiOff';
import RefreshIcon from '@mui/icons-material/Refresh';
import { AlertTypes } from '../../../../types/alert.types';

// Thresholds: the zone is polled every 30s, so "stale" means we've missed
// at least two expected polls in a row (60s+ since the last successful fetch).
const STALE_THRESHOLD_MS = 60_000;

const BLUE = '#206FA5';

const TYPE_COLORS: Record<string, string> = {
    SOS: '#D32F2F',
    CheckOnMe: '#64748B',
    FakeCall: '#64748B',
    RiskReview: '#64748B',
    Shift: '#64748B',
    Appointment: '#206FA5',
};

const TYPE_LABELS: Record<string, string> = {
    SOS: 'SOS',
    CheckOnMe: 'Check On Me',
    FakeCall: 'Fake Call',
    RiskReview: 'Risk Review',
    Shift: 'Shift',
    Appointment: 'Appointment',
};

function timeAgo(dateStr: string): string {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
}

interface ActiveAlertsZoneProps {
    alerts: AlertTypes.Model[];
    loading: boolean;
    onAlertClick: (alert: AlertTypes.Model) => void;
    /** Epoch ms of the last successful active-events fetch. 0 = never. */
    lastFetchAt?: number;
    /** True when the most recent fetch failed; surfaces an inline error
     *  when we have no cached list to show. Staleness chip handles the
     *  case where we have old data and a recent failure. */
    hasError?: boolean;
    onRetry?: () => void;
}

export const ActiveAlertsZone: React.FC<ActiveAlertsZoneProps> = ({ alerts, loading, onAlertClick, lastFetchAt = 0, hasError = false, onRetry }) => {
    // Staleness: only flag once we have at least one successful fetch under
    // our belt. Otherwise the mount-window render would always be "stale".
    const now = Date.now();
    const ageMs = lastFetchAt > 0 ? now - lastFetchAt : 0;
    const isStale = lastFetchAt > 0 && ageMs > STALE_THRESHOLD_MS;
    const ageMins = Math.floor(ageMs / 60_000);
    const staleLabel = ageMins >= 1
        ? `Data ${ageMins}m old`
        : 'Data stale';

    return (
        <Box sx={{
            width: '100%',
            bgcolor: '#fff',
            border: '1px solid #E8ECF0',
            borderRadius: '14px',
            p: '22px 22px 16px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
        }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 3, height: 16, borderRadius: 1, bgcolor: BLUE }} />
                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#64748B', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
                        Active Events
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {isStale && (
                        <Tooltip title="The live-events feed hasn't updated in a while. The operator may be working from stale data.">
                            <Chip
                                icon={<WifiOffIcon sx={{ fontSize: '14px !important' }} />}
                                label={staleLabel}
                                size="small"
                                sx={{
                                    fontSize: 10, fontWeight: 700, height: 22,
                                    bgcolor: '#E6510015', color: '#E65100',
                                    border: '1px solid #E6510030',
                                    '& .MuiChip-icon': { color: '#E65100', ml: 0.5 },
                                }}
                            />
                        </Tooltip>
                    )}
                    {alerts.length > 0 && (
                        <Chip
                            label={`${alerts.length} active`}
                            size="small"
                            sx={{
                                fontSize: 10, fontWeight: 700, height: 22,
                                bgcolor: '#D32F2F15', color: '#D32F2F',
                                border: '1px solid #D32F2F30',
                            }}
                        />
                    )}
                </Box>
            </Box>

            {loading ? (
                <Box sx={{ py: 4, textAlign: 'center' }}>
                    <Typography sx={{ color: '#94A3B8', fontSize: 13 }}>Loading alerts...</Typography>
                </Box>
            ) : hasError && alerts.length === 0 ? (
                // First-load failure (or every-load failure when we've never
                // had data) — surface this explicitly so "no events" isn't
                // silently rendered in the safety-critical all-quiet form.
                <Alert
                    severity="error"
                    action={onRetry ? (
                        <Button size="small" color="inherit" startIcon={<RefreshIcon />} onClick={onRetry}>
                            Retry
                        </Button>
                    ) : undefined}
                    sx={{ my: 1 }}
                >
                    Couldn’t load live events. Retry or wait for the next poll.
                </Alert>
            ) : alerts.length === 0 ? (
                <Box sx={{ py: 4, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                    <CheckCircleOutlineIcon sx={{ fontSize: 36, color: '#16A34A' }} />
                    <Typography sx={{ color: '#64748B', fontSize: 14, fontWeight: 500 }}>
                        No active events
                    </Typography>
                    <Typography sx={{ color: '#94A3B8', fontSize: 12 }}>
                        All quiet — nothing happening right now.
                    </Typography>
                </Box>
            ) : (
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 700, fontSize: 11, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: '1px solid #E8ECF0' }}>
                                User Name
                            </TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: 11, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: '1px solid #E8ECF0' }}>
                                Type
                            </TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: 11, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: '1px solid #E8ECF0' }}>
                                Time
                            </TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: 11, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: '1px solid #E8ECF0' }}>
                                Status
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {alerts.map((alert) => {
                            const typeColor = TYPE_COLORS[alert.alertType] || '#64748B';
                            const typeLabel = TYPE_LABELS[alert.alertType] || alert.alertType;
                            const isSOS = alert.alertType === 'SOS';
                            const userName = alert.user
                                ? `${alert.user.firstName || ''} ${alert.user.lastName || ''}`.trim()
                                : 'Unknown';

                            return (
                                <TableRow
                                    key={alert.id}
                                    hover
                                    onClick={() => onAlertClick(alert)}
                                    sx={{
                                        cursor: 'pointer',
                                        bgcolor: isSOS ? '#D32F2F06' : 'transparent',
                                        '&:hover': { bgcolor: isSOS ? '#D32F2F0F' : undefined },
                                    }}
                                >
                                    <TableCell sx={{ borderBottom: '1px solid #E8ECF0', py: 1.25 }}>
                                        <Typography sx={{ fontSize: 13, fontWeight: 500, color: '#1A2B3C' }}>
                                            {userName}
                                        </Typography>
                                    </TableCell>
                                    <TableCell sx={{ borderBottom: '1px solid #E8ECF0', py: 1.25 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            {isSOS && (
                                                <Box sx={{
                                                    width: 7, height: 7, borderRadius: '50%', bgcolor: '#D32F2F',
                                                    animation: 'pulse 1.5s ease-in-out infinite',
                                                    '@keyframes pulse': {
                                                        '0%, 100%': { opacity: 1, boxShadow: '0 0 0 0 #D32F2F80' },
                                                        '50%': { opacity: 0.8, boxShadow: '0 0 0 4px #D32F2F00' },
                                                    },
                                                }} />
                                            )}
                                            <Chip
                                                label={typeLabel}
                                                size="small"
                                                sx={{
                                                    fontSize: 11, fontWeight: 700, height: 22,
                                                    bgcolor: `${typeColor}15`, color: typeColor,
                                                    border: `1px solid ${typeColor}30`,
                                                }}
                                            />
                                        </Box>
                                    </TableCell>
                                    <TableCell sx={{ borderBottom: '1px solid #E8ECF0', py: 1.25 }}>
                                        <Typography sx={{ fontSize: 12, color: '#64748B', fontVariantNumeric: 'tabular-nums' }}>
                                            {timeAgo(alert.initiatedAt)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell sx={{ borderBottom: '1px solid #E8ECF0', py: 1.25 }}>
                                        {(() => {
                                            // Status chip color: red only when the row is urgent (SOS or
                                            // escalated CheckOnMe). Non-urgent Active → blue. Resolved → green.
                                            const isEscalated = !!alert.escalated;
                                            const isResolved = alert.status === 'resolved';
                                            const urgent = isSOS || isEscalated;
                                            const color = urgent ? '#D32F2F' : isResolved ? '#16A34A' : '#206FA5';
                                            return (
                                                <Chip
                                                    label={isEscalated ? 'Escalated' : alert.status}
                                                    size="small"
                                                    sx={{
                                                        fontSize: 10, fontWeight: 600, height: 20,
                                                        bgcolor: `${color}15`,
                                                        color,
                                                        textTransform: 'capitalize',
                                                    }}
                                                />
                                            );
                                        })()}
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            )}
        </Box>
    );
};
